import React from 'react';
import PropTypes from 'prop-types';

import * as Styled from './SlidingPanelDrawer.styled';
import OverlayModal from '../ContentSystem/components/OverlayModal/OverlayModal';
import { SlidingPanelsProvider } from '../SlidingPanel';

/**
 * A group of sliding panels in a modal
 *
 * This component is mainly for smaller viewports
 * since the Modal includes a withPeeking prop which
 * gives is a "drawer" animation.
 *
 * This component utlizes React context API in `SlidingPanels.context`
 * to control navigation between multiple SlidingPanels.
 *
 * @example
 *
 *  <SlidingPanelDrawer>
 *    <SlidingPanel />
 *    <SlidingPanel />
 *    <SlidingPanel />
 *  </SlidingPanelDrawer>
 *
 */
const SlidingPanelDrawer = ({
  children,
  initialActivePanels,
  isOpen,
  customRoot,
  onEveryClose,
  dataLabel,
  closeDataLabel,
  backDataLabel
}) => (
  <OverlayModal
    showHeaderCloseButton={false}
    withPadding={false}
    modalType="peeking"
    isOpen={isOpen}
    withCollapse
    hasHeaderDivider={false}
    dataLabel={dataLabel}
    closeDataLabel={closeDataLabel}
    backDataLabel={backDataLabel}
    onRequestClose={() => {
      onEveryClose();
    }}
  >
    <Styled.SlidingPanelDrawer>
      <SlidingPanelsProvider
        initialActivePanels={initialActivePanels}
        customRoot={customRoot}
        isOpen
        onEveryClose={onEveryClose}
      >
        {children}
      </SlidingPanelsProvider>
    </Styled.SlidingPanelDrawer>
  </OverlayModal>
);

SlidingPanelDrawer.propTypes = {
  /**
   * Child elements
   */
  children: PropTypes.node,
  /**
   * Panels that should start in the active state on mount
   */
  initialActivePanels: PropTypes.arrayOf(PropTypes.string),
  /**
   * Whether or not modal is open
   */
  isOpen: PropTypes.bool,
  /**
   * Specifies a custom root panel, passed as a panel id, if it isn't the first one
   */
  customRoot: PropTypes.string,
  /**
   * Handler when close icon is clicked on
   * any of the SlidingPanels
   *
   * @params {SyntheticBaseEvent} e React synthetic event
   */
  onEveryClose: PropTypes.func,
  /**
   * A data-label attribute for the modal root
   */
  dataLabel: PropTypes.string,
  /**
   * A data-label attribute for the close button
   */
  closeDataLabel: PropTypes.string,
  /**
   * A data-label attribute for the back button
   */
  backDataLabel: PropTypes.string
};

SlidingPanelDrawer.defaultProps = {
  children: null,
  initialActivePanels: [],
  isOpen: false,
  customRoot: null,
  onEveryClose: null,
  dataLabel: 'modal',
  closeDataLabel: 'modal_close',
  backDataLabel: 'modal_back'
};

export default SlidingPanelDrawer;
